// src/hooks/useAuthGate.ts
//
// ADR-025 (Accepted 2026-06-22): SPA-internal auth gate.
//
// Subscribes to:
//   1. The v12-auth-401 event (frozen 2026-06-12 in authEvents.ts),
//      emitted by authClient.fetchWithAuth() on any 401 response.
//   2. An initial /api/auth/status probe on mount, so a fresh navigate
//      to a protected page immediately redirects if the session is dead.
//
// On trigger:
//   - If window.location.pathname is in `protectedPaths`, dispatch the
//     'spa:navigate' event (consumed by Router in routes.tsx) to move
//     to /login?from=<original>. This is SPA-internal navigation — no
//     window.location, no full-page reload, so runtime state
//     (FrameCache, WS stream, ranking/positions) is preserved.
//
// Why hook (not router-level check) — per ADR-025 §4 R4:
//   The 6/21 ORPHAN decision explicitly forbids router-level auth
//   checks. The hook is mounted from main.tsx, not Router.
//
// Why opt-in (not blanket):
//   The dashboard `/` page is intentionally public (it is a kiosk view
//   for the trading floor). Pages that need auth opt in by adding
//   `useAuthGate({ protectedPaths: ['/<route>'] })` at the top of
//   their component.
//
// Edge cases:
//   - Initial probe: while /api/auth/status is in flight, the hook is
//     in 'loading' state. Protected pages should render a small
//     placeholder (or the existing skeleton) during this window — the
//     hook does NOT block the page render; the redirect only fires
//     after status resolves.
//   - Status probe failure (network blip): hook stays in 'loading' and
//     does NOT redirect. A 401 on a real fetch will fire the gate.
//   - Already on /login: hook is a no-op (avoids redirect loops).

import { useEffect, useRef, useState } from 'react';
import { authClient } from '../api/authClient';
import { AUTH_401_EVENT, type Auth401EventDetail } from '../api/authEvents';

export const SPA_NAVIGATE_EVENT = 'v14-spa-navigate';

export interface UseAuthGateOptions {
  /** Paths that require an active session. The hook redirects to
   *  /login?from=<path> when auth is missing on mount or 401 fires
   *  while on one of these paths. */
  protectedPaths: string[];
}

export interface UseAuthGateResult {
  /** True until the initial /api/auth/status probe completes. Pages
   *  may use this to suppress a flash of protected content. */
  loading: boolean;
  /** Latest known auth state from /api/auth/status. `null` while
   *  loading or if the probe failed. */
  authenticated: boolean | null;
}

function isProtected(path: string, protectedPaths: string[]): boolean {
  return protectedPaths.some((p) => path === p || path.startsWith(p + '/'));
}

function buildLoginHref(fromPath: string): string {
  return `/login?from=${encodeURIComponent(fromPath)}`;
}

function spaNavigate(toHref: string): void {
  // Mirror the click handler in routes.tsx:65 — pushState + dispatch
  // the v14-spa-navigate event so the Router can update its path state.
  window.history.pushState({}, '', toHref);
  window.dispatchEvent(
    new CustomEvent(SPA_NAVIGATE_EVENT, { detail: { to: toHref } }),
  );
}

export function useAuthGate(opts: UseAuthGateOptions): UseAuthGateResult {
  const { protectedPaths } = opts;
  const [loading, setLoading] = useState(true);
  const [authenticated, setAuthenticated] = useState<boolean | null>(null);
  // Use a ref to capture the latest protectedPaths in event handlers
  // without re-subscribing on every render.
  const protectedRef = useRef(protectedPaths);
  protectedRef.current = protectedPaths;

  useEffect(() => {
    let cancelled = false;

    // (1) Initial probe — /api/auth/status is whitelisted in
    //     PUBLIC_PATHS_EXACT (line 66 of _session_auth_wire.py) so it
    //     is reachable pre-auth.
    void (async () => {
      try {
        const s = await authClient.status();
        if (cancelled) return;
        setAuthenticated(s.authenticated);
        if (
          !s.authenticated &&
          isProtected(window.location.pathname, protectedRef.current)
        ) {
          spaNavigate(buildLoginHref(window.location.pathname + window.location.search));
        }
      } catch {
        // Network blip — leave `authenticated` null and let real 401s
        // drive the gate. Do not redirect on probe failure.
        if (!cancelled) setAuthenticated(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    // (2) Subscribe to v12-auth-401 — fired by authClient.fetchWithAuth
    //     on any 401 response. This catches BOTH the initial probe
    //     case (already covered by (1)) and any post-mount expiry.
    const on401 = (e: Event) => {
      const detail = (e as CustomEvent<Auth401EventDetail>).detail;
      const from = detail?.from ?? window.location.pathname + window.location.search;
      if (isProtected(window.location.pathname, protectedRef.current)) {
        setAuthenticated(false);
        spaNavigate(buildLoginHref(from));
      }
    };
    window.addEventListener(AUTH_401_EVENT, on401);

    return () => {
      cancelled = true;
      window.removeEventListener(AUTH_401_EVENT, on401);
    };
  }, []); // protectedPaths changes are picked up via ref

  return { loading, authenticated };
}
